import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { apiConfigSelector } from '../redux/reducers/apiConfigReducer';
import { brandGroupConfigSelector } from '../redux/reducers/brandGroupConfigReducer';
import { BrandGroupConfig } from '../types/apiConfig';
import { QUERY_PARAMS } from '../types/schemas/registerCompletionSchema';
import { toValidExtParam } from '../utils/extParams';
import { returnUrlPolicy, toAllowedReturnUrl } from '../utils/returnUrl';

type QueryParams = {
  hasSettled: boolean;
  state: string | null;
  code: string | null;
  brand: string | null;
  applicationUrl: string | null;
  articleId: string | null;
  utmInternal: string | null;
  cookieId: string | null;
  sessionId: string | null;
  language: string | null;
  email: string | null;
  consentId: string | null;
  initiateLoginUri: string | null;
  location: string | null;
  screenType: string | null;
  traceId: string | null;
  error: string | null;
  name: string | null;
  inviteId: string | null;
  errorCode: string | null;
  flowId: string | null;
};

export function useQueryParams(): QueryParams {
  const [searchParams] = useSearchParams();
  const { search } = useLocation();
  const brandGroups: BrandGroupConfig[] = useSelector(brandGroupConfigSelector);
  const { devReturnUrlDomains } = useSelector(apiConfigSelector);
  const policy = useMemo(
    () => returnUrlPolicy(brandGroups, devReturnUrlDomains),
    [brandGroups, devReturnUrlDomains]
  );

  // Necessary query params
  const [state, setState] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [brand, setBrand] = useState<string | null>(null);
  const [hasSettled, setHasSettled] = useState(false);

  // Progressive profiling query params
  const [applicationUrl, setApplicationUrl] = useState<string | null>(null);
  const [articleId, setArticleId] = useState<string | null>(null);
  const [location, setLocation] = useState<string | null>(null);

  // Account consent query params
  const [language, setLanguage] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [consentId, setConsentId] = useState<string | null>(null);
  const [initiateLoginUri, setInitiateLoginUri] = useState<string | null>(null);

  // Index query params
  const [screenType, setScreenType] = useState<string | null>(null);

  // tracking query params
  const [cookieId, setCookieId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [utmInternal, setUtmInternal] = useState<string | null>(null);

  // Spotify error param
  const [traceId, setTraceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Invitation UI params
  const [name, setName] = useState<string | null>(null);
  const [inviteId, setInviteId] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // FusionAuth Worker flow continuation
  const [flowId, setFlowId] = useState<string | null>(null);

  useEffect(() => {
    setState(searchParams.get(QUERY_PARAMS.STATE));
    setCode(searchParams.get(QUERY_PARAMS.CODE));
    setBrand(
      searchParams.get(QUERY_PARAMS.BRAND_ID) ||
        toValidExtParam(searchParams.get(QUERY_PARAMS.BRAND_EXT_ID))
    );
    setApplicationUrl(toAllowedReturnUrl(searchParams.get(QUERY_PARAMS.APPLICATION_URL), policy));
    const articleIdExt = toValidExtParam(searchParams.get(QUERY_PARAMS.ARTICLE_ID_EXT));
    const articleId = searchParams.get(QUERY_PARAMS.ARTICLE_ID);
    setArticleId(articleId || articleIdExt);
    setUtmInternal(toValidExtParam(searchParams.get(QUERY_PARAMS.UTM_INTERNAL)));
    setCookieId(toValidExtParam(searchParams.get(QUERY_PARAMS.COOKIE_ID)));
    setSessionId(toValidExtParam(searchParams.get(QUERY_PARAMS.SESSION_ID)));
    setLanguage(searchParams.get(QUERY_PARAMS.UI_LOCALES));
    setEmail(searchParams.get(QUERY_PARAMS.EMAIL));
    setConsentId(searchParams.get(QUERY_PARAMS.CONSENT_ID));
    setInitiateLoginUri(
      toAllowedReturnUrl(searchParams.get(QUERY_PARAMS.INITIATE_LOGIN_URI), policy)
    );
    setLocation(toAllowedReturnUrl(searchParams.get(QUERY_PARAMS.LOCATION), policy));
    setScreenType(searchParams.get(QUERY_PARAMS.SCREEN_TYPE));
    setTraceId(searchParams.get(QUERY_PARAMS.TRACE_ID));
    setError(searchParams.get(QUERY_PARAMS.ERROR));
    setName(searchParams.get(QUERY_PARAMS.NAME));
    setInviteId(searchParams.get(QUERY_PARAMS.INVITE_ID));
    setErrorCode(searchParams.get('errorCode'));
    setFlowId(searchParams.get(QUERY_PARAMS.FLOW_ID));
    setHasSettled(true);
  }, [search, searchParams, policy]);

  return {
    hasSettled,
    state,
    code,
    brand,
    applicationUrl,
    articleId,
    utmInternal,
    cookieId,
    sessionId,
    language,
    email,
    consentId,
    initiateLoginUri,
    location,
    screenType,
    traceId,
    error,
    name,
    inviteId,
    errorCode,
    flowId,
  };
}
